import {Injectable} from "@angular/core";
import {ImageCache} from "../caches/image-cache";
import {ActivatedRoute} from "@angular/router";
import {RenderedScenario, ScenarioRenderer} from "./scenario-renderer";
import {Corners, RenderOptions} from "../types";

@Injectable({
  providedIn: 'root'
})
export class ShadowInpaintingRenderer {
  constructor(
    private route: ActivatedRoute,
    private readonly imageCache: ImageCache) {
  }

  public async drawScenarioShadowImage({scenario, renderingContext, doorCorners, renderOptions}: {
    renderingContext: CanvasRenderingContext2D,
    doorCorners: Corners,
    scenario: RenderedScenario,
    renderOptions: RenderOptions
  }): Promise<void> {
    if (!scenario.shadowImageUrl) {
      return;
    }

    try {
      const shadowMask = await this.imageCache.get({url: scenario.shadowImageUrl});

      /*
        - 'multiply': The pixels are multiplied. A darker picture is the result.
        - 'screen': The pixels are inverted, multiplied, and inverted again. A lighter picture is the result (opposite of multiply)
          - Maybe this is an option if we can cancel out the black pixels

        - (default)  'overlay' is also nice insteaad of multiply
        - 'soft-light' is also nice!!
        - 'hard-light' -> to hard light
       */

      renderingContext.globalCompositeOperation = this.getCompositionOperator({ renderOptions });

      ScenarioRenderer.clipCorners({renderingContext, corners: doorCorners})

      renderingContext.drawImage(shadowMask, 0, 0, scenario.image.width, scenario.image.height);

      // Reset
      renderingContext.globalCompositeOperation = 'source-over';
      renderingContext.resetTransform()
    } catch (e) {
      console.error('Error during drawing shadow image', {
        shadowImageUrl: scenario.shadowImageUrl,
        error: e
      })
    }
  }

  private getCompositionOperator({ renderOptions }: {renderOptions: RenderOptions }): GlobalCompositeOperation {
    const snapshot = this.route.snapshot

    // Read out scenario id from url
    const compositionOperatorFromUrl = snapshot.queryParams['compositionOperator'];

    let globalCompositeOperation = renderOptions.compositionOperator || compositionOperatorFromUrl || localStorage.getItem('compositionOperator') || 'overlay'

    if (!this.isValidGlobalCompositeOperation(globalCompositeOperation)) {
      console.error('Invalid global composite operation: ' + globalCompositeOperation, '\nUsing default value: "multiply"', '\n\nValid values are: ' + this.allowedCompositionOperators.join(', '), '\nDocumented values: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation');
      globalCompositeOperation = 'multiply'
    }

    return globalCompositeOperation;
  }

  private allowedCompositionOperators = [
    "source-over", "source-in", "source-out", "source-atop",
    "estination-over", "destination-in", "destination-out", "destination-atop",
    "lighter", "copy", "xor", "multiply", "screen", "overlay",
    "darken", "lighten", "color-dodge", "color-burn", "hard-light",
    "soft-light", "difference", "exclusion", "hue", "saturation",
    "color", "luminosity"
  ]

  private isValidGlobalCompositeOperation(value: string): value is GlobalCompositeOperation {
    return this.allowedCompositionOperators.includes(value);
  }
}
